// ===============================================================
// ⭐ SettingsPage.js — إعدادات Livora (FINAL CLEAN VERSION)
// ===============================================================

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./SettingsPage.css";

import {
  FaChevronRight,
  FaBellSlash,
  FaEyeSlash,
  FaBan,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaGlobe,
  FaHeadset,
  FaTicketAlt,
  FaComment,
  FaExclamationTriangle,
  FaShieldAlt,
  FaFileAlt,
  FaTrash,
  FaCog,
  FaUser,
} from "react-icons/fa";

import TopBar from "../components/common/TopBar";
import { useTranslation } from "react-i18next";

import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const LANGUAGE_LABELS = {
  ar: "العربية",
  en: "English",
  de: "Deutsch",
  tr: "Türkçe",
  ru: "Русский",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
  fa: "فارسی",
  id: "Indonesia",
  ph: "Filipino",
  el: "Ελληνικά",
  he: "עברית",
  ku: "Kurdî",
};

function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [openPrivacy] = useState(true);
  const [openAccount] = useState(true);
  const [openLanguage] = useState(true);

  const [hideLastSeen, setHideLastSeen] = useState(false);
  const [doNotDisturb, setDoNotDisturb] = useState(false);

  const currentEmail =
    auth.currentUser?.email ||
    t("not_linked", { defaultValue: "غير مرتبط" });

  const currentPhone =
    auth.currentUser?.phoneNumber ||
    t("not_linked", { defaultValue: "غير مرتبط" });

  const currentLanguageLabel = useMemo(() => {
    const code = String(i18n.language || "en").split("-")[0];
    return LANGUAGE_LABELS[code] || code;
  }, [i18n.language]);

  useEffect(() => {
    const loadSettings = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data() || {};
          setHideLastSeen(data.hideLastSeen || false);
          setDoNotDisturb(data.doNotDisturb || false);
        }
      } catch (error) {
        console.error("loadSettings error:", error);
      }
    };

    loadSettings();
  }, []);

  const toggleHideLastSeen = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const newVal = !hideLastSeen;
    setHideLastSeen(newVal);

    try {
      await updateDoc(doc(db, "users", user.uid), {
        hideLastSeen: newVal,
      });
    } catch (error) {
      console.error("toggleHideLastSeen error:", error);
      setHideLastSeen(!newVal);
    }
  };

  const toggleDoNotDisturb = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const newVal = !doNotDisturb;
    setDoNotDisturb(newVal);

    try {
      await updateDoc(doc(db, "users", user.uid), {
        doNotDisturb: newVal,
      });
    } catch (error) {
      console.error("toggleDoNotDisturb error:", error);
      setDoNotDisturb(!newVal);
    }
  };

  return (
    <div className="settings-root">
      <TopBar
        title={t("settings.title", { defaultValue: "Settings" })}
        onBack={() => navigate(-1)}
      />

      <header className="settings-hero">
        <div className="settings-hero-inner">
          <div className="settings-hero-text">
            <h1 className="settings-hero-title">
              {t("settings.title", { defaultValue: "Settings" })}
            </h1>
            <p className="settings-hero-subtitle">
              {t("settings.subtitle", {
                defaultValue:
                  "تحكم كامل في خصوصيتك وتجربة استخدامك",
              })}
            </p>
          </div>
          <span className="settings-hero-icon" aria-hidden>
            <FaCog />
          </span>
        </div>
      </header>

      <div className="settings-content">
        {openPrivacy ? (
          <section className="settings-section-card" aria-labelledby="settings-privacy-heading">
            <div className="settings-section-head">
              <span
                className="settings-section-head-icon settings-section-head-icon--privacy"
                aria-hidden
              >
                <FaLock />
              </span>
              <h2 id="settings-privacy-heading">
                {t("privacy", { defaultValue: "الخصوصية" })}
              </h2>
            </div>
            <div className="settings-section-body">
              <div
                className="settings-row clickable"
                onClick={() => navigate("/settings/blocklist")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/settings/blocklist");
                  }
                }}
              >
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                    <FaBan />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("block_list", { defaultValue: "قائمة الحظر" })}
                    </span>
                    <span className="settings-row-subtitle">
                      {t("settings.block_list_sub", {
                        defaultValue: "إدارة المستخدمين المحظورين",
                      })}
                    </span>
                  </div>
                </div>
                <FaChevronRight className="settings-row-arrow" aria-hidden />
              </div>

              <div className="settings-row">
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                    <FaEyeSlash />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("hide_last_seen", { defaultValue: "إخفاء آخر ظهور" })}
                    </span>
                    <span className="settings-row-subtitle">
                      {t("settings.hide_last_seen_sub", {
                        defaultValue: "إخفاء وقت آخر ظهور عن الآخرين",
                      })}
                    </span>
                  </div>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={hideLastSeen}
                    onChange={toggleHideLastSeen}
                  />
                  <span className="settings-switch-slider" />
                </label>
              </div>

              <div className="settings-row">
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                    <FaBellSlash />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("do_not_disturb", { defaultValue: "عدم الإزعاج" })}
                    </span>
                    <span className="settings-row-subtitle">
                      {t("settings.dnd_sub", {
                        defaultValue: "إيقاف الإشعارات مؤقتاً",
                      })}
                    </span>
                  </div>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={doNotDisturb}
                    onChange={toggleDoNotDisturb}
                  />
                  <span className="settings-switch-slider" />
                </label>
              </div>
            </div>
          </section>
        ) : null}

        {openAccount ? (
          <section className="settings-section-card" aria-labelledby="settings-account-heading">
            <div className="settings-section-head">
              <span
                className="settings-section-head-icon settings-section-head-icon--account"
                aria-hidden
              >
                <FaUser />
              </span>
              <h2 id="settings-account-heading">
                {t("account.title", { defaultValue: "الحساب" })}
              </h2>
            </div>
            <div className="settings-section-body">
              <div
                className="settings-row clickable"
                onClick={() => navigate("/settings/change-email")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/settings/change-email");
                  }
                }}
              >
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--pink" aria-hidden>
                    <FaEnvelope />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("change_email", { defaultValue: "تغيير البريد الإلكتروني" })}
                    </span>
                    <span className="settings-row-subtitle">{currentEmail}</span>
                  </div>
                </div>
                <FaChevronRight className="settings-row-arrow" aria-hidden />
              </div>

              <div
                className="settings-row clickable"
                onClick={() => navigate("/settings/change-phone")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/settings/change-phone");
                  }
                }}
              >
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--pink" aria-hidden>
                    <FaPhone />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("change_phone", { defaultValue: "تغيير رقم الهاتف" })}
                    </span>
                    <span className="settings-row-subtitle">{currentPhone}</span>
                  </div>
                </div>
                <FaChevronRight className="settings-row-arrow" aria-hidden />
              </div>

              <div
                className="settings-row clickable"
                onClick={() => navigate("/settings/change-password")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/settings/change-password");
                  }
                }}
              >
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                    <FaLock />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("change_password", { defaultValue: "تغيير كلمة المرور" })}
                    </span>
                    <span className="settings-row-subtitle">
                      {t("settings.change_password_sub", {
                        defaultValue: "تحديث كلمة مرور تسجيل الدخول",
                      })}
                    </span>
                  </div>
                </div>
                <FaChevronRight className="settings-row-arrow" aria-hidden />
              </div>
            </div>
          </section>
        ) : null}

        {openLanguage ? (
          <section className="settings-section-card" aria-labelledby="settings-language-heading">
            <div className="settings-section-head">
              <span
                className="settings-section-head-icon settings-section-head-icon--language"
                aria-hidden
              >
                <FaGlobe />
              </span>
              <h2 id="settings-language-heading">
                {t("language", { defaultValue: "اللغة" })}
              </h2>
            </div>
            <div className="settings-section-body">
              <div
                className="settings-row clickable"
                onClick={() => navigate("/settings/language")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/settings/language");
                  }
                }}
              >
                <div className="settings-row-main">
                  <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                    <FaGlobe />
                  </span>
                  <div className="settings-row-texts">
                    <span className="settings-row-title">
                      {t("language", { defaultValue: "اللغة" })}
                    </span>
                    <span className="settings-row-subtitle">
                      {t("settings.language_sub", {
                        defaultValue: "تغيير لغة التطبيق",
                      })}
                    </span>
                  </div>
                </div>
                <div className="settings-row-trailing">
                  <span className="settings-row-value">{currentLanguageLabel}</span>
                  <FaChevronRight className="settings-row-arrow" aria-hidden />
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="settings-section-card" aria-labelledby="settings-support-heading">
          <div className="settings-section-head">
            <span
              className="settings-section-head-icon settings-section-head-icon--support"
              aria-hidden
            >
              <FaHeadset />
            </span>
            <h2 id="settings-support-heading">
              {t("settings.support_section", {
                defaultValue: "الدعم والمساعدة",
              })}
            </h2>
          </div>

          <div className="settings-section-body">
            <div
              className="settings-row clickable"
              onClick={() => navigate("/support/history")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/support/history");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                  <FaTicketAlt />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("support.tickets", { defaultValue: "تذاكر الدعم" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.support_tickets_sub", {
                      defaultValue: "متابعة طلباتك السابقة",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>

            <div
              className="settings-row clickable"
              onClick={() => navigate("/support/new/contact")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/support/new/contact");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--blue" aria-hidden>
                  <FaComment />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("contact_us", { defaultValue: "تواصل معنا" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.contact_sub", {
                      defaultValue: "راسل فريق الدعم",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>

            <div
              className="settings-row clickable"
              onClick={() => navigate("/support/new/issue")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/support/new/issue");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--pink" aria-hidden>
                  <FaExclamationTriangle />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("report_problem", { defaultValue: "الإبلاغ عن مشكلة" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.report_sub", {
                      defaultValue: "أبلغ عن خطأ أو سلوك غير لائق",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>

            <div
              className="settings-row clickable"
              onClick={() => navigate("/privacy")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/privacy");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                  <FaShieldAlt />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("privacy_policy", { defaultValue: "سياسة الخصوصية" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.privacy_sub", {
                      defaultValue: "كيف نتعامل مع بياناتك",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>

            <div
              className="settings-row clickable"
              onClick={() => navigate("/terms")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/terms");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--purple" aria-hidden>
                  <FaFileAlt />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("terms_of_use", { defaultValue: "شروط الاستخدام" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.terms_sub", {
                      defaultValue: "قواعد استخدام المنصة",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>
          </div>
        </section>

        <section
          className="settings-section-card settings-section-card--danger"
          aria-labelledby="settings-danger-heading"
        >
          <div className="settings-section-head">
            <span
              className="settings-section-head-icon settings-section-head-icon--danger"
              aria-hidden
            >
              <FaExclamationTriangle />
            </span>
            <h2 id="settings-danger-heading">
              {t("settings.danger_zone", { defaultValue: "منطقة الخطر" })}
            </h2>
          </div>
          <div className="settings-section-body">
            <div
              className="settings-row settings-row--danger clickable"
              onClick={() => navigate("/settings/delete-account")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/settings/delete-account");
                }
              }}
            >
              <div className="settings-row-main">
                <span className="settings-row-icon settings-row-icon--danger" aria-hidden>
                  <FaTrash />
                </span>
                <div className="settings-row-texts">
                  <span className="settings-row-title">
                    {t("delete_account", { defaultValue: "حذف الحساب" })}
                  </span>
                  <span className="settings-row-subtitle">
                    {t("settings.delete_sub", {
                      defaultValue: "حذف حسابك بشكل نهائي",
                    })}
                  </span>
                </div>
              </div>
              <FaChevronRight className="settings-row-arrow" aria-hidden />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SettingsPage;
